import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, CornerDownLeft } from "lucide-react";
import { MENUITEMS } from "../../Sidebar/Menu";
import { iconMap } from "../../Sidebar/SidebarMenuItems";
import styles from "./HeaderSearch.module.scss";

// Header arama — TEK-TIK açılır, kendine yeten dropdown (eski full-width "Search
// Cuba" overlay'i + çift-tık bug'lı Searchbar SÖKÜLDÜ). Menü linklerini filtreler,
// notification/messages dropdown'larıyla tutarlı görünüm.
const flattenLinks = (items = [], acc = []) => {
  items.forEach((it) => {
    if (it.type === "link" && it.path) {
      acc.push({ title: it.title, path: it.path, icon: it.icon });
    }
    if (Array.isArray(it.children)) flattenLinks(it.children, acc);
  });
  return acc;
};

const ALL_LINKS = MENUITEMS.flatMap((sec) => flattenLinks(sec.Items || []));

const HeaderSearch = ({ className = "" }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Panel body'ye PORTAL'lanır + position:fixed → header'ın stacking context'ine
  // (z-index 8 + backdrop-filter) hapsolmaz; aksi halde altındaki kart sızıyordu.
  const computePos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: Math.round(r.bottom + 10), right: Math.round(window.innerWidth - r.right) });
  }, []);

  const results = useMemo(() => {
    const k = query.trim().toLowerCase();
    const matches = k
      ? ALL_LINKS.filter((i) => {
          const a = String(i.title || "").toLowerCase();
          const b = String(t(i.title) || "").toLowerCase();
          return a.includes(k) || b.includes(k);
        })
      : ALL_LINKS;
    return matches.slice(0, 8);
  }, [query, t]);

  useEffect(() => {
    if (open) {
      computePos();
      const id = setTimeout(() => inputRef.current?.focus(), 30);
      const onWin = () => computePos();
      window.addEventListener("resize", onWin);
      window.addEventListener("scroll", onWin, true);
      return () => {
        clearTimeout(id);
        window.removeEventListener("resize", onWin);
        window.removeEventListener("scroll", onWin, true);
      };
    }
    setQuery("");
    setActiveIdx(0);
    return undefined;
  }, [open, computePos]);

  useEffect(() => setActiveIdx(0), [query]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open]);

  const select = (path) => {
    if (!path) return;
    navigate(path);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      select(results[activeIdx]?.path);
    }
  };

  return (
    <li className={`${className} ${styles.host}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((o) => !o)}
        aria-label={t("Search")}
        aria-expanded={open}
      >
        <Search className="header-icon" />
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            className={styles.panel}
            style={{ "--panel-top": `${pos.top}px`, "--panel-right": `${pos.right}px` }}
            role="dialog"
          >
            <div className={styles.searchRow}>
              <Search size={17} className={styles.searchIcon} />
              <input
                ref={inputRef}
                className={styles.input}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={t("Search pages…")}
                type="text"
              />
            </div>

            <div className={styles.results}>
              {results.length === 0 ? (
                <div className={styles.empty}>{t("No results found")}</div>
              ) : (
                results.map((r, i) => {
                  const Icon = iconMap[r.icon];
                  return (
                    <button
                      key={r.path + i}
                      type="button"
                      className={`${styles.result} ${
                        i === activeIdx ? styles.resultActive : ""
                      }`}
                      onMouseEnter={() => setActiveIdx(i)}
                      onClick={() => select(r.path)}
                    >
                      {Icon ? (
                        <Icon size={16} className={styles.resultIcon} />
                      ) : (
                        <span className={styles.resultIcon} />
                      )}
                      <span className={styles.resultTitle}>{t(r.title)}</span>
                      {i === activeIdx && (
                        <CornerDownLeft size={14} className={styles.enterHint} />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </li>
  );
};

export default HeaderSearch;
